'use client'

import { GoogleAdsAddKeywordsDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsAddKeywordsDisplay'
import { GoogleAdsAdGroupManagementDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsAdGroupManagementDisplay'
import { GoogleAdsAdManagementDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsAdManagementDisplay'
import { GoogleAdsAssetGroupManagementDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsAssetGroupManagementDisplay'
import { GoogleAdsCalloutManagementDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsCalloutManagementDisplay'
import { GoogleAdsCampaignManagementDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsCampaignManagementDisplay'
import { GoogleAdsPauseKeywordsDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsPauseKeywordsDisplay'
import { GoogleAdsRemoveKeywordsDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsRemoveKeywordsDisplay'
import { GoogleAdsSitelinkManagementDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsSitelinkManagementDisplay'
import { GoogleAdsStructuredSnippetManagementDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsStructuredSnippetManagementDisplay'
import { GoogleAdsUpdateBudgetDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsUpdateBudgetDisplay'
import { GoogleAdsUpdateKeywordsDisplay } from '@/components/WorkflowToolResults/Google/GoogleAdsUpdateKeywordsDisplay'
import {
  extractErrorFromNodeExecution,
  getErrorTitle,
  mapErrorToUserFriendly,
} from '@/lib/utils/errorMapping'
import { Fit, Layout, useRive } from '@rive-app/react-webgl2'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import type { WorkflowToolIds } from 'sprites-types/workflow/tools/common'
import { AppStoreMetadataDisplay } from './AppStore/AppStoreMetadataDisplay'
import { AppStoreReviewsDisplay } from './AppStore/AppStoreReviewsDisplay'
import { WorkflowWebSearchDisplay } from './Exa/WorkflowWebSearchDisplay'
import { FacebookAdsAdManagementDisplay } from './Facebook/FacebookAdsAdManagementDisplay'
import { FacebookAdsAdSetManagementDisplay } from './Facebook/FacebookAdsAdSetManagementDisplay'
import { FacebookAdsCampaignManagementDisplay } from './Facebook/FacebookAdsCampaignManagementDisplay'
import { FacebookAdsGetAdDetailsDisplay } from './Facebook/FacebookAdsGetAdDetailsDisplay'
import { FacebookAdsGetAdSetDetailsDisplay } from './Facebook/FacebookAdsGetAdSetDetailsDisplay'
import { FacebookAdsGetCampaignDetailsDisplay } from './Facebook/FacebookAdsGetCampaignDetailsDisplay'
import { FacebookAdsGetCreativeDisplay } from './Facebook/FacebookAdsGetCreativeDisplay'
import { FacebookAdsLeadgenFormListDisplay } from './Facebook/FacebookAdsLeadgenFormListDisplay'
import { FacebookAdsLeadgenFormManagementDisplay } from './Facebook/FacebookAdsLeadgenFormManagementDisplay'
import { FacebookAdsListAccountsDisplay } from './Facebook/FacebookAdsListAccountsDisplay'
import { FacebookAdsPixelListDisplay } from './Facebook/FacebookAdsPixelListDisplay'
import { FacebookAdsPixelManagementDisplay } from './Facebook/FacebookAdsPixelManagementDisplay'
import { FacebookAdsSearchTargetingDisplay } from './Facebook/FacebookAdsSearchTargetingDisplay'
import { FetchFromUrlDisplay } from './FetchFromUrl/FetchFromUrlDisplay'
import { FramerActionsDisplay } from './Framer/FramerActionsDisplay'
import { FramerWebPublisherDisplay } from './Framer/FramerWebPublisherDisplay'
import { GmailAddLabelToEmailDisplay } from './Gmail/GmailAddLabelToEmailDisplay'
import { GmailArchiveEmailDisplay } from './Gmail/GmailArchiveEmailDisplay'
import { GmailCreateDraftDisplay } from './Gmail/GmailCreateDraftDisplay'
import { GmailDownloadAttachmentDisplay } from './Gmail/GmailDownloadAttachmentDisplay'
import { GmailFindEmailDisplay } from './Gmail/GmailFindEmailDisplay'
import { GmailListLabelsDisplay } from './Gmail/GmailListLabelsDisplay'
import { GmailRemoveLabelFromEmailDisplay } from './Gmail/GmailRemoveLabelFromEmailDisplay'
import { GmailSendEmailDisplay } from './Gmail/GmailSendEmailDisplay'
import { GmailUpdateOrgSignatureDisplay } from './Gmail/GmailUpdateOrgSignatureDisplay'
import { GmailUpdatePrimarySignatureDisplay } from './Gmail/GmailUpdatePrimarySignatureDisplay'
import { GoogleAdsFetchDisplay } from './Google/GoogleAdsFetchDisplay'
import { GoogleAdsGetAdDetailsDisplay } from './Google/GoogleAdsGetAdDetailsDisplay'
import { GoogleAdsGetAdGroupDetailsDisplay } from './Google/GoogleAdsGetAdGroupDetailsDisplay'
import { GoogleAdsGetAssetDetailsDisplay } from './Google/GoogleAdsGetAssetDetailsDisplay'
import { GoogleAdsGetAssetGroupDetailsDisplay } from './Google/GoogleAdsGetAssetGroupDetailsDisplay'
import { GoogleAdsGetCampaignDetailsDisplay } from './Google/GoogleAdsGetCampaignDetailsDisplay'
import { GoogleAdsGetExtensionDetailsDisplay } from './Google/GoogleAdsGetExtensionDetailsDisplay'
import { GoogleAdsGetKeywordDetailsDisplay } from './Google/GoogleAdsGetKeywordDetailsDisplay'
import { GoogleAdsGetPlacementDetailsDisplay } from './Google/GoogleAdsGetPlacementDetailsDisplay'
import { GoogleAdsGetProductDetailsDisplay } from './Google/GoogleAdsGetProductDetailsDisplay'
import { GoogleAdsGetProductGroupDetailsDisplay } from './Google/GoogleAdsGetProductGroupDetailsDisplay'
import { GoogleAdsGetSearchQueryDetailsDisplay } from './Google/GoogleAdsGetSearchQueryDetailsDisplay'
import { GoogleAdsInsightsDisplay } from './Google/GoogleAdsInsightsDisplay'
import { GoogleAdsListCampaignsDisplay } from './Google/GoogleAdsListResourcesDisplay'
import { GoogleSearchConsoleListSitesDisplay } from './Google/GoogleSearchConsoleListSitesDisplay'
import { GoogleSearchConsolePerformanceMetricsDisplay } from './Google/GoogleSearchConsolePerformanceMetricsDisplay'
import { GoogleSearchConsoleUrlInspectionDisplay } from './Google/GoogleSearchConsoleUrlInspectionDisplay'
import { GoogleDriveCreateFileFromTextDisplay } from './GoogleDrive/GoogleDriveCreateFileFromTextDisplay'
import { GoogleDriveCreateFolderDisplay } from './GoogleDrive/GoogleDriveCreateFolderDisplay'
import { GoogleDriveDownloadFileDisplay } from './GoogleDrive/GoogleDriveDownloadFileDisplay'
import { GoogleDriveFindFileDisplay } from './GoogleDrive/GoogleDriveFindFileDisplay'
import { GoogleDriveFindFolderDisplay } from './GoogleDrive/GoogleDriveFindFolderDisplay'
import { GoogleDriveGetFileByIdDisplay } from './GoogleDrive/GoogleDriveGetFileByIdDisplay'
import { GoogleDriveListFilesDisplay } from './GoogleDrive/GoogleDriveListFilesDisplay'
import { GoogleDriveMoveFileDisplay } from './GoogleDrive/GoogleDriveMoveFileDisplay'
import { GoogleDriveUploadFileDisplay } from './GoogleDrive/GoogleDriveUploadFileDisplay'
import { GraphicsCreatorDisplay } from './Graphics/GraphicsCreatorDisplay'
import { GraphicsCreatorV2Display } from './Graphics/GraphicsCreatorV2Display'
import { InstagramFetchPostsDisplay } from './Instagram/InstagramFetchPostsDisplay'
import { InstagramFetchProfileDisplay } from './Instagram/InstagramFetchProfileDisplay'
import { InstagramScraperDisplay } from './Instagram/InstagramScraperDisplay'
import { InstagramSearchDisplay } from './Instagram/InstagramSearchDisplay'
import { LinkedinCreatePostDisplay } from './LinkedIn/LinkedinCreatePostDisplay'
import { LinkedinGetAdsDisplay } from './LinkedIn/LinkedinGetAdsDisplay'
import { LinkedinGetCurrentMemberProfileDisplay } from './LinkedIn/LinkedinGetCurrentMemberProfileDisplay'
import { LinkedinGetProfilesDisplay } from './LinkedIn/LinkedinGetProfilesDisplay'
import { LinkedinSearchOrganizationDisplay } from './LinkedIn/LinkedinSearchOrganizationDisplay'
import { LinkedinSearchPostsDisplay } from './LinkedIn/LinkedinSearchPostsDisplay'
import { FacebookAdsInsightsDisplay } from './Meta/FacebookAdsInsightsDisplay'
import { MetaAdsFetchDisplay } from './Meta/MetaAdsFetchDisplay'
import { NativeApprovalDisplay } from './Native/NativeApprovalDisplay'
import { NativeBackgroundRemovalDisplay } from './Native/NativeBackgroundRemovalDisplay'
import { NativeBatchDisplay } from './Native/NativeBatchDisplay'
import { NativeCodeRunnerDisplay } from './Native/NativeCodeRunnerDisplay'
import { NativeCsvLlmActionDisplay } from './Native/NativeCsvLlmActionDisplay'
import { NativeCsvReaderDisplay } from './Native/NativeCsvReaderDisplay'
import { NativeCsvRowActionDisplay } from './Native/NativeCsvRowActionDisplay'
import { NativeCsvWriterDisplay } from './Native/NativeCsvWriterDisplay'
import { NativeFileUploadDisplay } from './Native/NativeFileUploadDisplay'
import { NativeFilterDisplay } from './Native/NativeFilterDisplay'
import { NativeFlattenDisplay } from './Native/NativeFlattenDisplay'
import { NativeLlmImageGenerationDisplay } from './Native/NativeLlmImageGenerationDisplay'
import { NativeLlmResponseTextDisplay } from './Native/NativeLlmResponseTextDisplay'
import { NativeVideoUnderstandingDisplay } from './Native/NativeVideoUnderstandingDisplay'
import { SeoArticleUpdaterDisplay } from './Native/SeoArticleUpdaterDisplay'
import { SeoArticleWriterDisplay } from './Native/SeoArticleWriterDisplay'
import { SitemapperDisplay } from './Native/SitemapperDisplay'
import { PlaywrightGotoDisplay } from './Playwright/PlaywrightGotoDisplay'
import { PlaywrightScreenshotDisplay } from './Playwright/PlaywrightScreenshotDisplay'
import { RedditSearchCommentsDisplay } from './Reddit/RedditSearchCommentsDisplay'
import { RedditSearchDisplay } from './Reddit/RedditSearchDisplay'
import { SeoDomainAnalysisDisplay } from './Seo/SeoDomainAnalysisDisplay'
import { SeoKeywordsDisplay } from './Seo/SeoKeywordsDisplay'
import { SeoUrlAnalysisDisplay } from './Seo/SeoUrlAnalysisDisplay'
import { AiVisibilityDisplay } from './SeoReviewTools/AiVisibilityDisplay'
import { SearchTrafficEstimateDisplay } from './SeoReviewTools/SearchTrafficEstimateDisplay'
import { SeoBacklinksDisplay } from './SeoReviewTools/SeoBacklinksDisplay'
import { SeoTechnicalAuditDisplay } from './SeoReviewTools/SeoTechnicalAuditDisplay'
import type { WorkflowToolComponent } from './shared/WorkflowToolComponent'
import { ShopifyWebPublisherDisplay } from './Shopify/ShopifyWebPublisherDisplay'
import { RecentTweetsDisplay } from './Twitter/RecentTweetsDisplay'
import { TwitterPostDisplay } from './Twitter/TwitterPostDisplay'
import { WebflowActionsDisplay } from './Webflow/WebflowActionsDisplay'
import { WordPressActionsDisplay } from './WordPress/WordPressActionsDisplay'
import { WordPressGetPageDisplay } from './WordPress/WordPressGetPageDisplay'
import { WordPressPublishPageDisplay } from './WordPress/WordPressPublishPageDisplay'
import { WordPressUpdatePageDisplay } from './WordPress/WordPressUpdatePageDisplay'
import { WordPressWebPublisherDisplay } from './WordPress/WordPressWebPublisherDisplay'
import { YoutubeCaptionsDisplay } from './Youtube/YoutubeCaptionsDisplay'
import { YoutubeDetailsDisplay } from './Youtube/YoutubeDetailsDisplay'
import { YoutubeSearchDisplay } from './Youtube/YoutubeSearchDisplay'

interface Props {
  nodeExecution: NodeExecution
  workflowConfig: WorkflowConfig
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
}

interface WorkflowToolErrorDisplayProps {
  nodeExecution: NodeExecution
  toolId: string
}

function WorkflowLoadingAnimation() {
  const { RiveComponent } = useRive({
    src: '/animation/final-light.riv',
    stateMachines: 'State Machine 1',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
    }),
  })

  return (
    <div className="h-10 w-10 flex-shrink-0 overflow-hidden">
      <div className="h-full w-full scale-[3]">
        <RiveComponent />
      </div>
    </div>
  )
}

function WorkflowToolErrorDisplay({ nodeExecution, toolId }: WorkflowToolErrorDisplayProps) {
  const rawErrorMessage = extractErrorFromNodeExecution(nodeExecution.outputs)
  const errorMapping = mapErrorToUserFriendly(rawErrorMessage)
  const errorTitle = getErrorTitle(errorMapping.category)

  // Get appropriate styling based on error category
  const getErrorStyling = () => {
    switch (errorMapping.category) {
      case 'validation':
        return {
          border: 'border-yellow-600',
          background: 'bg-yellow-50',
          titleColor: 'text-yellow-800',
          messageColor: 'text-yellow-700',
          iconColor: 'text-yellow-600',
        }
      case 'auth':
        return {
          border: 'border-blue-600',
          background: 'bg-blue-50',
          titleColor: 'text-blue-800',
          messageColor: 'text-blue-700',
          iconColor: 'text-blue-600',
        }
      case 'credits':
        return {
          border: 'border-orange-600',
          background: 'bg-orange-50',
          titleColor: 'text-orange-800',
          messageColor: 'text-orange-700',
          iconColor: 'text-orange-600',
        }
      default:
        return {
          border: 'border-red-600',
          background: 'bg-red-50',
          titleColor: 'text-red-800',
          messageColor: 'text-red-700',
          iconColor: 'text-red-600',
        }
    }
  }

  const styling = getErrorStyling()

  return (
    <div className={`rounded-lg border ${styling.border} ${styling.background} p-4`}>
      <div className="flex items-start gap-3">
        <svg
          className={`mt-0.5 h-5 w-5 ${styling.iconColor} flex-shrink-0`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="min-w-0 flex-1">
          <h3 className={`font-medium ${styling.titleColor} mb-1`}>
            {errorTitle} - {toolId}
          </h3>
          <p className={`${styling.messageColor} text-sm leading-relaxed`}>
            {errorMapping.userFriendlyMessage}
          </p>

          {/* Show technical details in development mode only */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                Technical Details
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs text-gray-700">
                {JSON.stringify(nodeExecution.outputs, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  )
}

// This will throw a compile error if any WorkflowToolIds are missing
const TOOL_COMPONENT_MAP: Record<
  WorkflowToolIds,
  React.ComponentType<{ nodeExecution: NodeExecution; onOpenPanel?: any }> | WorkflowToolComponent
> = {
  // SEO Review Tools
  aiVisibility: AiVisibilityDisplay,

  // App Store
  appStoreMetadata: AppStoreMetadataDisplay,
  appStoreReviews: AppStoreReviewsDisplay,

  // Framer
  framerActions: FramerActionsDisplay,
  framerWebPublisher: FramerWebPublisherDisplay,

  // Gmail
  gmailAddLabelToEmail: GmailAddLabelToEmailDisplay,
  gmailArchiveEmail: GmailArchiveEmailDisplay,
  gmailCreateDraft: GmailCreateDraftDisplay,
  gmailDownloadAttachment: GmailDownloadAttachmentDisplay,
  gmailFindEmail: GmailFindEmailDisplay,
  gmailListLabels: GmailListLabelsDisplay,
  gmailRemoveLabelFromEmail: GmailRemoveLabelFromEmailDisplay,
  gmailSendEmail: GmailSendEmailDisplay,
  gmailUpdateOrgSignature: GmailUpdateOrgSignatureDisplay,
  gmailUpdatePrimarySignature: GmailUpdatePrimarySignatureDisplay,

  // Google
  googleAdsAddKeywords: GoogleAdsAddKeywordsDisplay,
  googleAdsAdGroupManagement: GoogleAdsAdGroupManagementDisplay,
  googleAdsGetAdGroupDetails: GoogleAdsGetAdGroupDetailsDisplay,
  googleAdsAdManagement: GoogleAdsAdManagementDisplay,
  googleAdsGetAdDetails: GoogleAdsGetAdDetailsDisplay,
  googleAdsAssetGroupManagement: GoogleAdsAssetGroupManagementDisplay,
  googleAdsGetAssetGroupDetails: GoogleAdsGetAssetGroupDetailsDisplay,
  googleAdsGetAssetDetails: GoogleAdsGetAssetDetailsDisplay,
  googleAdsCalloutManagement: GoogleAdsCalloutManagementDisplay,
  googleAdsGetKeywordDetails: GoogleAdsGetKeywordDetailsDisplay,
  googleAdsCampaignManagement: GoogleAdsCampaignManagementDisplay,
  googleAdsGetCampaignDetails: GoogleAdsGetCampaignDetailsDisplay,
  googleAdsFetch: GoogleAdsFetchDisplay,
  googleAdsInsights: GoogleAdsInsightsDisplay,
  googleAdsListCampaigns: GoogleAdsListCampaignsDisplay,
  googleAdsPauseKeywords: GoogleAdsPauseKeywordsDisplay,
  googleAdsGetPlacementDetails: GoogleAdsGetPlacementDetailsDisplay,
  googleAdsGetProductGroupDetails: GoogleAdsGetProductGroupDetailsDisplay,
  googleAdsGetProductDetails: GoogleAdsGetProductDetailsDisplay,
  googleAdsRemoveKeywords: GoogleAdsRemoveKeywordsDisplay,
  googleAdsGetSearchQueryDetails: GoogleAdsGetSearchQueryDetailsDisplay,
  googleAdsGetExtensionDetails: GoogleAdsGetExtensionDetailsDisplay,
  googleAdsSitelinkManagement: GoogleAdsSitelinkManagementDisplay,
  googleAdsStructuredSnippetManagement: GoogleAdsStructuredSnippetManagementDisplay,
  googleAdsUpdateBudget: GoogleAdsUpdateBudgetDisplay,
  googleAdsUpdateKeywords: GoogleAdsUpdateKeywordsDisplay,

  // Google Search Console
  googleSearchConsoleUrlInspection: GoogleSearchConsoleUrlInspectionDisplay,
  googleSearchConsolePerformanceMetrics: GoogleSearchConsolePerformanceMetricsDisplay,
  googleSearchConsoleListSites: GoogleSearchConsoleListSitesDisplay,

  // Google Drive
  googleDriveCreateFileFromText: GoogleDriveCreateFileFromTextDisplay,
  googleDriveCreateFolder: GoogleDriveCreateFolderDisplay,
  googleDriveDownloadFile: GoogleDriveDownloadFileDisplay,
  googleDriveFindFile: GoogleDriveFindFileDisplay,
  googleDriveFindFolder: GoogleDriveFindFolderDisplay,
  googleDriveGetFileById: GoogleDriveGetFileByIdDisplay,
  googleDriveListFiles: GoogleDriveListFilesDisplay,
  googleDriveMoveFile: GoogleDriveMoveFileDisplay,
  googleDriveUploadFile: GoogleDriveUploadFileDisplay,

  // Graphics
  graphicsCreator: GraphicsCreatorDisplay,
  graphicsCreatorV2: GraphicsCreatorV2Display,

  // Instagram
  instagramFetchPosts: InstagramFetchPostsDisplay,
  instagramFetchProfile: InstagramFetchProfileDisplay,
  instagramScraper: InstagramScraperDisplay,
  instagramSearch: InstagramSearchDisplay,

  // LinkedIn
  linkedInSearchPosts: LinkedinSearchPostsDisplay, // Note: ID has capital I
  linkedinCreatePost: LinkedinCreatePostDisplay,
  linkedinGetAds: LinkedinGetAdsDisplay,
  linkedinGetCurrentMemberProfile: LinkedinGetCurrentMemberProfileDisplay,
  linkedinGetProfiles: LinkedinGetProfilesDisplay,
  linkedinSearchOrganization: LinkedinSearchOrganizationDisplay,

  // Facebook
  facebookAdsAdManagement: FacebookAdsAdManagementDisplay,
  facebookAdsGetAdDetails: FacebookAdsGetAdDetailsDisplay,
  facebookAdsAdSetManagement: FacebookAdsAdSetManagementDisplay,
  facebookAdsGetAdSetDetails: FacebookAdsGetAdSetDetailsDisplay,
  facebookAdsCampaignManagement: FacebookAdsCampaignManagementDisplay,
  facebookAdsGetCampaignDetails: FacebookAdsGetCampaignDetailsDisplay,
  facebookAdsGetCreative: FacebookAdsGetCreativeDisplay,
  facebookAdsCreateCustomAudience: DefaultDisplay,
  facebookAdsUpdateCustomAudience: DefaultDisplay,
  facebookAdsCreateLookalikeAudience: DefaultDisplay,
  facebookAdsLeadgenFormManagement: FacebookAdsLeadgenFormManagementDisplay,
  facebookAdsLeadgenFormList: FacebookAdsLeadgenFormListDisplay,
  facebookAdsListAccounts: FacebookAdsListAccountsDisplay,
  facebookAdsListAudiences: DefaultDisplay,
  facebookAdsSearchTargeting: FacebookAdsSearchTargetingDisplay,
  facebookAdsPixelList: FacebookAdsPixelListDisplay,
  facebookAdsPixelManagement: FacebookAdsPixelManagementDisplay,

  // Fetch
  fetchFromUrl: FetchFromUrlDisplay,

  // Meta
  metaAdsFetch: MetaAdsFetchDisplay,
  facebookAdsInsights: FacebookAdsInsightsDisplay,

  // Native
  nativeApproval: NativeApprovalDisplay,
  nativeBackgroundRemoval: NativeBackgroundRemovalDisplay,
  nativeBatch: NativeBatchDisplay,
  nativeCodeRunner: NativeCodeRunnerDisplay,
  nativeCsvLlmAction: NativeCsvLlmActionDisplay,
  nativeCsvReader: NativeCsvReaderDisplay,
  nativeCsvRowAction: NativeCsvRowActionDisplay,
  nativeCsvWriter: NativeCsvWriterDisplay,
  nativeMerge: DefaultDisplay,
  nativeSort: DefaultDisplay,
  nativeRouter: DefaultDisplay,
  nativeFileUpload: NativeFileUploadDisplay,
  nativeFilter: NativeFilterDisplay,
  nativeFlatten: NativeFlattenDisplay,
  nativeImageGenerate: NativeLlmImageGenerationDisplay,
  nativeLlmResponseText: NativeLlmResponseTextDisplay,
  nativeVideoUnderstanding: NativeVideoUnderstandingDisplay,

  // Playwright
  playwrightGoto: PlaywrightGotoDisplay,
  playwrightScreenshot: PlaywrightScreenshotDisplay,

  // Reddit
  redditSearch: RedditSearchDisplay,
  redditSearchComments: RedditSearchCommentsDisplay,

  // SEO Review Tools
  seoBacklinks: SeoBacklinksDisplay,

  // SEO Tools
  seoDomainAnalysis: SeoDomainAnalysisDisplay,
  seoKeywords: SeoKeywordsDisplay,
  seoUrlAnalysis: SeoUrlAnalysisDisplay,

  // SEO/Content
  searchTrafficEstimate: SearchTrafficEstimateDisplay,
  seoArticleUpdater: SeoArticleUpdaterDisplay,
  seoArticleWriter: SeoArticleWriterDisplay,
  seoTechnicalAudit: SeoTechnicalAuditDisplay,
  seoKeywordOpportunityRanker: DefaultDisplay,
  sitemapper: SitemapperDisplay,

  // Shopify
  shopifyWebPublisher: ShopifyWebPublisherDisplay,

  // Twitter
  recentTweets: RecentTweetsDisplay,
  twitterPost: TwitterPostDisplay,

  // Web Search
  workflowWebSearch: WorkflowWebSearchDisplay,

  // Webflow
  webflowActions: WebflowActionsDisplay,

  // WordPress
  wordpressActions: WordPressActionsDisplay,
  wordpressWebPublisher: WordPressWebPublisherDisplay,
  wordpressGetPage: WordPressGetPageDisplay,
  wordpressPublishPage: WordPressPublishPageDisplay,
  wordpressUpdatePage: WordPressUpdatePageDisplay,

  // YouTube
  youtubeCaptions: YoutubeCaptionsDisplay,
  youtubeDetails: YoutubeDetailsDisplay,
  youtubeSearch: YoutubeSearchDisplay,
}

function DefaultDisplay({ nodeExecution }: { nodeExecution: NodeExecution }) {
  return (
    <div className="rounded-lg border bg-gray-50 p-4">
      <p className="mb-2 text-sm text-gray-600">Raw output:</p>
      <pre className="overflow-auto rounded border bg-white p-2 text-xs">
        {JSON.stringify(nodeExecution, null, 2)}
      </pre>
    </div>
  )
}

/**
 * Exports all workflow results to markdown format
 */
export function exportWorkflowToMarkdown(
  nodeExecutions: NodeExecution[],
  workflowConfig: WorkflowConfig
): string {
  const markdownSections: string[] = []

  // Add workflow header
  markdownSections.push(`# Workflow Results\n\n`)

  // Process each node execution
  for (const nodeExecution of nodeExecutions) {
    // Get toolId from node execution or fall back to workflow config
    let toolId = nodeExecution.toolId
    if (!toolId) {
      const node = workflowConfig.nodes.find((n) => n.id === nodeExecution.nodeId)
      toolId = node?.toolId
    }
    if (!toolId) continue

    const componentEntry = TOOL_COMPONENT_MAP[toolId as WorkflowToolIds]
    if (!componentEntry) continue

    // If this is a WorkflowToolComponent with markdown support
    if ('Component' in componentEntry && 'toMarkdown' in componentEntry) {
      markdownSections.push(componentEntry.toMarkdown(nodeExecution, workflowConfig))
    } else {
      // Legacy component - provide basic info
      markdownSections.push(
        [
          `## ${toolId}`,
          '',
          `**Node ID:** ${nodeExecution.nodeId}`,
          '',
          `**Status:** ${nodeExecution.status}`,
          '',
          '```json',
          JSON.stringify(nodeExecution.outputs, null, 2),
          '```',
          '',
          '---',
          '',
        ].join('\n')
      )
    }
  }

  return markdownSections.join('\n')
}

/**
 * Checks if a node execution has empty or missing outputs
 */
function hasEmptyOutputs(nodeExecution: NodeExecution): boolean {
  // If outputs is null or undefined, it's empty
  if (nodeExecution.outputs == null) {
    return true
  }

  // If outputs is an empty object, it's empty
  if (
    typeof nodeExecution.outputs === 'object' &&
    Object.keys(nodeExecution.outputs).length === 0
  ) {
    return true
  }

  // If outputs is an empty array, it's empty
  if (Array.isArray(nodeExecution.outputs) && nodeExecution.outputs.length === 0) {
    return true
  }

  return false
}

export function WorkflowToolResult({ nodeExecution, workflowConfig, onOpenPanel }: Props) {
  // Get tool ID from node execution (preferred) or fall back to workflow config
  let toolId = nodeExecution.toolId

  if (!toolId) {
    // Fall back to workflow config for backward compatibility
    const node = workflowConfig.nodes.find((n) => n.id === nodeExecution.nodeId)
    if (!node) {
      return (
        <div className="rounded-lg border bg-red-50 p-4 text-red-800">
          No node definition found for: {nodeExecution.nodeId}
        </div>
      )
    }
    toolId = node.toolId
  }

  if (!toolId) {
    return (
      <div className="rounded-lg border bg-yellow-50 p-4">
        <p className="mb-2 font-medium text-yellow-800">
          No tool ID found for node: {nodeExecution.nodeId}
        </p>
        <DefaultDisplay nodeExecution={nodeExecution} />
      </div>
    )
  }

  // Check if nodeExecution has failed
  if (nodeExecution.status === 'failed' || nodeExecution.status === 'errored') {
    return <WorkflowToolErrorDisplay nodeExecution={nodeExecution} toolId={toolId} />
  }

  // Check if still running
  if (nodeExecution.status === 'running' || nodeExecution.status === 'pending') {
    const statusText = nodeExecution.status === 'pending' ? 'Queued' : 'Running'

    return (
      <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <WorkflowLoadingAnimation />
          <div className="min-w-0 flex-1">
            <h3 className="mb-1 font-medium text-gray-900">
              {statusText} - {toolId}
            </h3>
            <p className="text-sm leading-relaxed text-gray-600">
              {nodeExecution.status === 'pending'
                ? 'This step is waiting to be processed'
                : 'This step is currently being executed'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Check if outputs are empty - return null to avoid showing empty spaces
  if (nodeExecution.status === 'completed' && hasEmptyOutputs(nodeExecution)) {
    return null
  }

  // Get the component for this tool
  const componentEntry = TOOL_COMPONENT_MAP[toolId as WorkflowToolIds]

  if (!componentEntry) {
    console.warn(`No display component found for tool: ${toolId}`)
    return (
      <div className="rounded-lg border bg-yellow-50 p-4">
        <p className="mb-2 font-medium text-yellow-800">No display component for: {toolId}</p>
        <DefaultDisplay nodeExecution={nodeExecution} />
      </div>
    )
  }

  // Determine if this is a WorkflowToolComponent or legacy React component
  const Component = 'Component' in componentEntry ? componentEntry.Component : componentEntry

  // Wrap component rendering in error boundary logic
  try {
    return (
      <div className="h-full w-full overflow-visible">
        <Component
          nodeExecution={nodeExecution}
          onOpenPanel={onOpenPanel}
          workflowConfig={workflowConfig}
        />
      </div>
    )
  } catch (error) {
    console.error(`Error rendering component for tool ${toolId}:`, error)
    return (
      <div className="rounded-lg border bg-red-50 p-4">
        <p className="mb-2 font-medium text-red-800">Error displaying {toolId} results</p>
        <p className="mb-2 text-sm text-red-600">
          {error instanceof Error ? error.message : 'Unknown error occurred'}
        </p>
        <DefaultDisplay nodeExecution={nodeExecution} />
      </div>
    )
  }
}

/**
 * Downloads all tool results for the provided node executions.
 * Uses each tool component's download function when available; skips legacy components.
 */
export async function downloadAllToolResults(
  nodeExecutions: NodeExecution[],
  workflowConfig: WorkflowConfig
): Promise<void> {
  // Iterate through executions in order and invoke tool-specific download handlers when available
  for (const nodeExecution of nodeExecutions) {
    try {
      // Get toolId from node execution or fall back to workflow config
      let toolId = nodeExecution.toolId
      if (!toolId) {
        const node = workflowConfig.nodes.find((n) => n.id === nodeExecution.nodeId)
        toolId = node?.toolId
      }
      if (!toolId) continue

      const componentEntry = TOOL_COMPONENT_MAP[toolId as WorkflowToolIds]
      if (!componentEntry) continue

      // Only WorkflowToolComponents expose a download function
      if ('download' in componentEntry && typeof componentEntry.download === 'function') {
        await componentEntry.download(nodeExecution)
      }
    } catch (error) {
      // Continue with next execution if one fails; errors are logged for visibility
      // Rationale: Best-effort download of all results
      console.error('Failed to download result for node', nodeExecution.nodeId, error)
    }
  }
}
